import {
    List,
    Datagrid,
    TextField,
    FunctionField, useNotify,
} from 'react-admin';
import { Box, Stack, Typography } from '@mui/material';
import { Button } from 'react-admin';
import { Switch } from '../switch/Switch.tsx';
import { useTranslation } from 'react-i18next';
import {useState} from "react";
import CourierService from "../../services/courier/courierService.ts";
import { useNavigate } from 'react-router-dom';
import { EmptyBox } from '../empty-box/EmptyBox.tsx';
import * as Sentry from '@sentry/react';

//TODO: move styles to a separate file if possible

const RiderTableActions = () => {
    const { t } = useTranslation();
    return (
        <Box
            sx={{
                paddingTop: 'var(--space-72)',
                paddingBottom: 'var(--space-32)',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Typography variant="h5" sx={{
                paddingLeft: 'var(--space-24)',
                fontWeight: 700,
                fontSize: 'var(--text-xxl)',
            }}>
                {t('resources.riderManagement')}
            </Typography>

        </Box>
    )
};

export const RiderTable = () => {
    const { t } = useTranslation();
    const courierService = new CourierService();
    const [switchStates, setSwitchStates] = useState<{[key: number]: boolean}>({});
    const notify = useNotify();
    const navigate = useNavigate();

    const lockUnLockCourier = (courierId: number, currentState: boolean) => async () => {

        setSwitchStates(prev => ({
            ...prev,
            [courierId]: !currentState
        }));

        try {
            await courierService.lockUnlockCourier(courierId);
        } catch (error) {
            setSwitchStates(prev => ({
                ...prev,
                [courierId]: currentState
            }));
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            Sentry.captureException(error);
        }
    }

    return (
        <List
            actions={<RiderTableActions />}
            perPage={25}
            queryOptions={{ refetchInterval: 6000 }}
            empty={<EmptyBox 
                title={t('riderManagement.emptyBox.title')} 
                description={t('riderManagement.emptyBox.description')} 
                resource='riderManagement'
                />}
            sx={{
                '& .RaList-main': { px: 4 },
                '& .RaList-content': {
                    border: '1px solid var(--color-secondary)',
                    borderRadius: 'var(--radius-6)',
                },
                '& .RaDatagrid-tbody tr:last-of-type td': {
                    borderBottom: 'none',
                },
                '& th.MuiTableCell-root': {
                    paddingLeft: 'var(--space-24)',
                    py: 'var(--space-16)',
                    fontSize: 'var(--text-xs)',
                },
                '& td.MuiTableCell-root': {
                    py: 'var(--space-24)',
                    paddingLeft: 'var(--space-24)',
                    fontSize: 'var(--text-xs)',
                },
                '& .MuiTablePagination-root': {
                    '& .MuiSelect-select': {
                        paddingRight: '32px !important',
                        paddingLeft: '12px !important',
                    },
                    '& .MuiSelect-icon': {
                        right: '8px',
                    }
                }
            }}
        >
            <Datagrid
                bulkActionButtons={false}
                rowClick={false}
                sx={{
                    '& .RaDatagrid-headerCell': {
                        fontWeight: 700,
                        letterSpacing: 0.2,
                    },
                }}
            >
                <TextField source="id" label={t('riderManagement.columns.id')} sortable={false} />
                <TextField source="firstName" label={t('riderManagement.columns.name')} sortable={false} />
                <TextField source="lastName" label={t('riderManagement.columns.surname')} sortable={false} />
                <TextField source="currentOrderNumber" label={t('riderManagement.columns.currentOrderNumber')} sortable={false} />
                <TextField source="activity" label={t('riderManagement.columns.activity')} sortable={false} />
                <TextField source="phoneNumber" label={t('riderManagement.columns.phone')} sortable={false} />
                <FunctionField
                    sortable={false}
                    label={t('riderManagement.columns.accountStatus')}
                    render={(record: any) => {
                        const originalStatus: boolean = record.isBlocked;

                        const currentStatus = switchStates[record.id] !== undefined
                            ? switchStates[record.id]
                            : originalStatus;

                        return (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Switch
                                    checked={currentStatus}
                                    color="primary"
                                    size="small"
                                    onChange={() => {
                                        lockUnLockCourier(record.id, currentStatus)();
                                    }}
                                />
                                <Typography sx={{ fontSize: 'var(--text-xxs)' }}>
                                    {currentStatus ? t('riderManagement.locked') : t('riderManagement.unlocked')}
                                </Typography>
                            </Stack>
                        );
                    }}
                />
                <FunctionField
                    sortable={false}
                    label={t('riderManagement.columns.availability')}
                    render={(record: any) => {
                        const isEnabled: boolean = Boolean(record.availability);
                        return (
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Switch
                                    checked={isEnabled}
                                    color="primary"
                                    size="small"
                                    disabled
                                    sx={{ cursor: 'not-allowed' }}
                                />
                                <Typography sx={{ fontSize: 'var(--text-xxs)' }}>
                                    {isEnabled ? t('riderManagement.active') : t('riderManagement.notActive')}
                                </Typography>
                            </Stack>
                        );
                    }}
                />
                <TextField source="agencyName" label={t('riderManagement.columns.agency')} sortable={false} />
                <TextField source="vehicleType" label={t('riderManagement.columns.typeOfVehicle')} sortable={false} />
                <FunctionField
                    label=""
                    sortable={false}
                    render={record => (
                        <>
                            <Button label={t('riderManagement.columns.performance')} style={{ fontSize: 'var(--text-xs)', backgroundColor: 'transparent', color: 'var(--color-primary)', fontWeight: '700' }} onClick={() => navigate(`/riderManagement/${record.id}/performance`)}/>
                        </>
                    )}
                />
            </Datagrid>
        </List>
    )
};